type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface ScopedLogger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  log: (...args: unknown[]) => void;
  child: (childScope: string) => ScopedLogger;
}

const disabledScopes = new Set<string>();
let globalMinLevel: LogLevel = __DEV__ ? 'debug' : 'warn';

const levelRank: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
};

export function setLoggerMinLevel(level: LogLevel): void {
  globalMinLevel = level;
}

export function disableLoggerScope(scope: string): void {
  disabledScopes.add(scope);
}

export function enableLoggerScope(scope: string): void {
  disabledScopes.delete(scope);
}

function shouldEmit(scope: string, level: LogLevel): boolean {
  if (disabledScopes.has(scope)) return false;
  return levelRank[level] >= levelRank[globalMinLevel];
}

export function createScopedLogger(scope: string): ScopedLogger {
  const prefix = `[${scope}]`;

  const emit = (level: LogLevel, consoleFn: (...a: unknown[]) => void) =>
    (...args: unknown[]) => {
      if (!shouldEmit(scope, level)) return;
      consoleFn(prefix, ...args);
    };

  return {
    debug: emit('debug', console.log),
    info: emit('info', console.log),
    warn: emit('warn', console.warn),
    error: emit('error', console.error),
    log: emit('info', console.log),
    child: (childScope: string) => createScopedLogger(`${scope}:${childScope}`),
  };
}
